// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SimpleLegendControl.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { GOOGLE_MAPS_CONSTANTS } from 'logic/data/Izenda.ReportPartMapConstants';
import tinycolor from 'tinycolor2';
import { times, get, some, isEmpty, isNumber } from 'lodash/fp';
import numeral from 'numeral';
import classnames from 'classnames';
import raf from 'raf';

const deltaScroll = 90;
const ONE_HUNDRED_PERCENT = 100;
const finishIntervalTime = 500;

const maxHeightPercent = 0.25;
// Default size of gradient bar. Should also change gradient bar size in the style sheet file to take affect
const defaultGradientBarSize = 160;
// Distance of the legend to the edge of map container having gradient bar in vertical layout
const paddingVerticalGradientBar = 70;
// Distance of the legend to the edge of map container having gradient bar in horizontal layout
const paddingHorizontalGradientBar = 55;
// Padding of gradient bar inside the legend
const legendVerticalPadding = 20;
// Number of portion in gradient bar
const GRADIENT_BAR_MARKER_PORTION = GOOGLE_MAPS_CONSTANTS.GRADIENT_BAR_MARKER_PORTION;
const columnWidth = 200;
const containerMargin = 5;
const containerPadding = 5;

const iconClassname = 'izenda-GoogleMaps-Legend-OptionIcon';
const labelClassname = 'izenda-GoogleMaps-Legend-OptionLabel';

export function updateDisplayStatus(node, display) {
  node.icon.className = classnames(iconClassname, {
    'izenda-GoogleMaps-Legend-OptionIcon_inactive': !display
  });
  node.label.className = classnames(labelClassname, {
    'izenda-GoogleMaps-Legend-OptionLabel_inactive': !display
  });
}

export default function SimpleLegendControl(map, hostNode, options, resolveDrawLegend) {
  const {
    legend: {
      backgroundColor,
      borderColor,
      borderWidth,
      layout,
      itemStyle,
      title,
      title: { style: titleStyle },
      series
    }
  } = options;
  let finishInterval = null;

  const container = document.createElement('div');
  const hasGradientBar = some(option => !!option.gradientRange, series);

  container.className = classnames('izenda-GoogleMaps-Legend', {
    'izenda-GoogleMaps-Legend_horizontal': layout === 'horizontal',
    'izenda-GoogleMaps-Legend-border': !!borderWidth
  });
  container.style.backgroundColor = backgroundColor;
  if (!!borderWidth) {
    container.style.borderColor = borderColor;
    container.style.borderWidth = `${borderWidth}px`;
  }
  container.style.margin = `${containerMargin}px`;
  container.style.padding = `${containerPadding}px`;

  hostNode.appendChild(container);
  if (title.text !== undefined) {
    const titleContainer = document.createElement('div');
    titleContainer.className = 'izenda-GoogleMaps-Legend-TitleContainer';
    const titleElement = document.createElement('div');
    titleElement.className = 'izenda-GoogleMaps-Legend-Title';

    setElementStyle(titleElement, Object.assign({}, titleStyle, { backgroundColor }));

    titleElement.innerText = title.text;
    titleElement.title = title.text;
    titleContainer.appendChild(titleElement);
    container.appendChild(titleContainer);
  }

  const optionsContainer = document.createElement('div');
  optionsContainer.className = classnames('izenda-GoogleMaps-Legend-OptionsContainer', {
    'izenda-GoogleMaps-Legend-OptionsContainer_horizontal': layout === 'horizontal',
    'izenda-GoogleMaps-Legend-OptionsContainer_vertical': layout !== 'horizontal'
  });

  const mapContainerHeight = map.getDiv().clientHeight;
  const mapContainerWidth = map.getDiv().clientWidth;
  if (layout === 'horizontal') {
    const countOfColumns = Math.min(
      Math.floor((mapContainerWidth - 2 * containerMargin - 2 * containerPadding) / columnWidth),
      series.length
    );
    optionsContainer.style.maxWidth = `${countOfColumns * columnWidth}px`;
  }

  const gradientBarSize =
    layout === 'vertical'
      ? getGradientBarSize(mapContainerHeight, paddingVerticalGradientBar)
      : getGradientBarSize(mapContainerWidth, paddingHorizontalGradientBar);

  optionsContainer.style.flexDirection = layout === 'horizontal' ? 'row' : 'column';
  optionsContainer.style.flexWrap = layout === 'horizontal' ? 'wrap' : 'nowrap ';
  optionsContainer.style.maxHeight = `${Math.max(
    hasGradientBar && layout === 'vertical' ? Math.min(mapContainerHeight, gradientBarSize + legendVerticalPadding) : 0,
    mapContainerHeight * maxHeightPercent
  )}px`;

  series.forEach(option => {
    const item = document.createElement('div');
    item.className = classnames('izenda-GoogleMaps-Legend-Option', {
      'izenda-GoogleMaps-Legend-Option_horizontal': layout === 'horizontal',
      u_paddingLeft: hasGradientBar && !option.gradientRange && layout === 'vertical'
    });
    item.style.width = `${columnWidth}px`;

    const itemContent = document.createElement('div');
    itemContent.title = option.label;

    if (option.gradientRange) {
      itemContent.className = classnames('izenda-GoogleMaps-Legend-OptionContent', 'gradient-bar', layout);
      const gradientBar = drawGradientBar(option.gradientRange, layout, itemStyle, gradientBarSize);
      itemContent.appendChild(gradientBar.element);
      this.showGradientCaret = gradientBar.showGradientCaret;
    } else {
      itemContent.className = classnames('izenda-GoogleMaps-Legend-OptionContent');
      const icon = document.createElement('span');
      icon.className = iconClassname;
      const backgroundColor = tinycolor(option.iconColor).setAlpha(
        option.type === 'bubbleSeries'
          ? GOOGLE_MAPS_CONSTANTS.BUBBLE_FILL_OPACITY
          : GOOGLE_MAPS_CONSTANTS.DEFAULT_SHADING_FILL_OPACITY
      );
      icon.style.backgroundColor = backgroundColor.toRgbString();
      icon.style.borderColor = option.iconColor;

      const label = document.createElement('span');
      label.className = labelClassname;
      label.innerText = option.label;

      setElementStyle(label, itemStyle);

      itemContent.appendChild(icon);
      itemContent.appendChild(label);

      !isEmpty(option.eventListeners) &&
        option.eventListeners.forEach(e => {
          item.addEventListener(e.eventName, e.eventHandler.bind(option, map, { icon, label }));
        });
    }

    item.appendChild(itemContent);
    optionsContainer.appendChild(item);
  });

  container.appendChild(optionsContainer);
  const updateArrowsStatus = renderArrows(container, optionsContainer);

  function finishProcess() {
    if (!!container.clientHeight) {
      updateArrowsStatus();
      resolveDrawLegend && resolveDrawLegend();
      clearInterval(finishInterval);
    }
  }

  finishInterval = setInterval(finishProcess, finishIntervalTime);
}

/**
 * Render arrows
 * @param {DOMNode} container
 * @param {DOMNode} optionsContainer
 */
function renderArrows(container: any, optionsContainer: any) {
  const arrowContainer = document.createElement('div');
  const upArrow = document.createElement('div');
  const downArrow = document.createElement('div');
  const baseUpArrowClassName = 'izenda-GoogleMaps-Legend-UpArrow icon-chevron-up';
  const baseDownArrowClassName = 'izenda-GoogleMaps-Legend-DownArrow icon-chevron-down';

  arrowContainer.className = 'izenda-GoogleMaps-Legend-ArrowContainer';
  upArrow.className = baseUpArrowClassName;
  downArrow.className = baseDownArrowClassName;

  upArrow.onclick = () => {
    smoothScroll(optionsContainer, -deltaScroll, updateArrowsStatus);
  };

  downArrow.onclick = () => {
    smoothScroll(optionsContainer, deltaScroll, updateArrowsStatus);
  };

  arrowContainer.appendChild(upArrow);
  arrowContainer.appendChild(downArrow);
  container.appendChild(arrowContainer);

  updateArrowsStatus();

  return updateArrowsStatus;

  function updateArrowsStatus() {
    const currentScrollTop = optionsContainer.scrollTop;
    upArrow.className = !currentScrollTop ? baseUpArrowClassName + ' disabled-elm' : baseUpArrowClassName;

    let downClass = baseDownArrowClassName;
    if (optionsContainer.scrollHeight > optionsContainer.clientHeight) {
      optionsContainer.scrollTop = optionsContainer.scrollTop + 1;
      const isDisable = currentScrollTop && currentScrollTop === optionsContainer.scrollTop;
      optionsContainer.scrollTop = currentScrollTop;

      isDisable && (downClass += ' disabled-elm');
    } else {
      downClass += ' disabled-elm';
    }
    downArrow.className = downClass;
  }
}

/**
 * Draw gradient bar
 * @param {Object} gradientRange
 * @param {String} layout
 */
function drawGradientBar(gradientRange: Object, layout: String, itemStyle: Object, gradientBarSize: Number) {
  const gradientBar = document.createElement('div');
  const isHorizontal = layout === 'horizontal';

  gradientBar.className = classnames('izenda-GoogleMaps-Legend-Option-GradientBar', layout);
  const backgroundImage = `linear-gradient(to ${isHorizontal ? 'right' : 'bottom'}, ${gradientRange.lowColor}, ${
    gradientRange.highColor
  })`;
  gradientBar.style.backgroundImage = backgroundImage;

  // Set the size of gradient bar
  const sizeProperty = layout === 'vertical' ? 'height' : 'width';
  gradientBar.style[sizeProperty] = `${gradientBarSize}px`;

  const markerIndicators = times(() => {
    const markerIndicator = document.createElement('div');
    markerIndicator.className = classnames('izenda-GoogleMaps-Legend-Option-MarkerIndicator', layout);
    return markerIndicator;
  }, GRADIENT_BAR_MARKER_PORTION + 1);

  const markers = times(() => {
    const marker = document.createElement('div');
    marker.className = classnames('izenda-GoogleMaps-Legend-Option-Marker', layout);
    return marker;
  }, GRADIENT_BAR_MARKER_PORTION + 1);

  const caret = document.createElement('span');
  caret.className = classnames('izenda-GoogleMaps-Legend-Option-GradientBarCaret', 'icon-filled-triangle-up', layout);

  const positionProperty = isHorizontal ? 'left' : 'top';
  const range = get('range[0]', gradientRange);

  const delta = range[1] - range[0];
  const highValue = Math.max(...range);
  const lowValue = Math.min(...range);

  // Place white indicator inside gradient bar
  markerIndicators.forEach((markerIndicator, index) => {
    markerIndicator.style[positionProperty] = `calc(${(ONE_HUNDRED_PERCENT / GRADIENT_BAR_MARKER_PORTION) *
      index}% - ${index}px)`;
  });

  // Place each marker's value
  markers.forEach((marker, index) => {
    marker.innerText = numeral((delta / GRADIENT_BAR_MARKER_PORTION) * index + range[0]).format('0.[0]a');
    marker.style[positionProperty] = `${(ONE_HUNDRED_PERCENT / GRADIENT_BAR_MARKER_PORTION) * index}%`;
    setElementStyle(marker, itemStyle);
  });

  // Show the caret of the gradient bar when hover shading
  const showGradientCaret = (isShow: Boolean, value: Number) => {
    if (isShow && (isNumber(value) || value === '')) {
      let percent;
      if (value <= lowValue) {
        percent = 0;
      } else if (value >= highValue) {
        percent = ONE_HUNDRED_PERCENT;
      } else {
        percent = ((value - lowValue) / delta) * ONE_HUNDRED_PERCENT;
      }

      // Range is reverse
      if (range[0] !== lowValue) {
        percent = ONE_HUNDRED_PERCENT - Math.abs(percent);
      }

      caret.style[positionProperty] = `${percent}%`;
      caret.style.visibility = 'visible';
    } else {
      caret.style.visibility = 'hidden';
    }
  };

  markerIndicators.forEach(markerIndicator => gradientBar.appendChild(markerIndicator));
  markers.forEach(marker => gradientBar.appendChild(marker));
  gradientBar.appendChild(caret);

  return { element: gradientBar, showGradientCaret };
}

function smoothScroll(element, delta, callback) {
  let start = element.scrollTop,
    easing = easeInOutQuad,
    distance = start + delta,
    duration = 500,
    timeStart,
    timeElapsed;

  raf(time => {
    timeStart = time;
    loop(time);
  });

  function loop(time) {
    timeElapsed = time - timeStart;

    element.scrollTop = easing(timeElapsed, start, delta, duration);

    if (timeElapsed < duration) raf(loop);
    else {
      element.scrollTop = distance;
      typeof callback === 'function' && callback();
    }
  }

  // Robert Penner's easeInOutQuad - http://robertpenner.com/easing/
  function easeInOutQuad(currentTime, startValue, changeInValue, duration) {
    currentTime /= duration / 2;
    if (currentTime < 1) return (changeInValue / 2) * currentTime * currentTime + startValue;
    currentTime--;
    return (-changeInValue / 2) * (currentTime * (currentTime - 2) - 1) + startValue;
  }
}

/**
 * Set element style
 * @param {HTMLElement} element
 * @param {Object} itemStyle
 */
function setElementStyle(element: HTMLElement, itemStyle: Object) {
  element.style.backgroundColor = itemStyle.backgroundColor;
  element.style.fontFamily = itemStyle.fontFamily;
  element.style.fontStyle = itemStyle.fontStyle;
  element.style.fontWeight = itemStyle.fontWeight;
  element.style.fontSize = itemStyle.fontSize;
  element.style.textDecoration = itemStyle.textDecoration;
  element.style.color = itemStyle.color;
}

function getGradientBarSize(limit: Number, padding: Number) {
  return Math.min(defaultGradientBarSize, limit - padding);
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/googlemaps/Izenda.SimpleLegendControl.js